import React, { useMemo } from 'react';
import { signals } from '../../mock';
import { Radar, Zap } from 'lucide-react';

// Colors cycle for signal nodes
const NODE_COLORS = ['#4ECDC4', '#4285F4', '#FF3B7F', '#FBBC05', '#34A853', '#9333EA', '#00E5FF'];

export default function Signals() {
  const width = 1100;
  const height = 520;

  // Position 15 nodes in a semi-circular / lattice pattern
  const nodes = useMemo(() => {
    return signals.map((s, i) => {
      const cols = 5;
      const rows = 3;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const padX = 100;
      const padY = 90;
      const stepX = (width - padX * 2) / (cols - 1);
      const stepY = (height - padY * 2) / (rows - 1);
      // add slight random-ish offset per node for organic feel
      const jitterX = ((i * 37) % 30) - 15;
      const jitterY = ((i * 53) % 30) - 15;
      return {
        label: s,
        x: padX + col * stepX + jitterX,
        y: padY + row * stepY + jitterY,
        color: NODE_COLORS[i % NODE_COLORS.length],
      };
    });
  }, []);

  // Curated connections between related signals
  const connections = useMemo(() => {
    // deterministic edges by index pair — connects each node to 2-3 nearby ones
    const edges = [];
    for (let i = 0; i < nodes.length; i++) {
      const nexts = [i + 1, i + 5, i + 4, i - 1, i + 6].filter((j) => j >= 0 && j < nodes.length && j !== i);
      nexts.slice(0, 3).forEach((j) => {
        // avoid duplicates
        const key = `${Math.min(i, j)}-${Math.max(i, j)}`;
        if (!edges.find((e) => e.key === key)) {
          edges.push({ key, a: nodes[i], b: nodes[j], color: nodes[i].color, delay: (i * 0.15 + j * 0.05) % 3 });
        }
      });
    }
    return edges;
  }, [nodes]);

  return (
    <section className="relative py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4] overflow-hidden">
      {/* glow */}
      <div className="absolute top-20 left-1/3 w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.5), transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <Radar className="w-3.5 h-3.5" />
              <span>Signal Intelligence</span>
            </div>
            <h2 className="text-5xl lg:text-7xl tracking-tight leading-[1.02] font-medium">
              <span className="tabular text-[#4ECDC4]">15</span> Signals.
              <br />
              <span className="font-serif italic font-normal">One connected mesh.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-white/60 text-[15px] leading-relaxed">A multi-dimensional view of every account. Each signal continuously informs every other — no data point is an island.</p>
            <div className="mt-6 flex items-center gap-2 text-[#4ECDC4] text-sm font-medium">
              <Zap className="w-4 h-4" />
              <span>We act on signals before your competitors see them.</span>
            </div>
          </div>
        </div>

        {/* Animated network of signals */}
        <div className="relative bg-[#0A1530] border border-white/10 rounded-2xl p-6 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(78,205,196,1) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,1) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          <svg viewBox={`0 0 ${width} ${height}`} className="relative w-full h-[420px] lg:h-[520px]">
            {/* connection lines */}
            {connections.map((e, i) => (
              <g key={e.key}>
                <line x1={e.a.x} y1={e.a.y} x2={e.b.x} y2={e.b.y} stroke="#4ECDC4" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="4 4" />
                {/* animated signal pulse traveling on the edge */}
                <circle r="3" fill={e.color}>
                  <animate attributeName="cx" from={e.a.x} to={e.b.x} dur="2.4s" begin={`${e.delay}s`} repeatCount="indefinite" />
                  <animate attributeName="cy" from={e.a.y} to={e.b.y} dur="2.4s" begin={`${e.delay}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1" dur="2.4s" begin={`${e.delay}s`} repeatCount="indefinite" />
                </circle>
              </g>
            ))}

            {/* nodes */}
            {nodes.map((n, i) => (
              <g key={n.label}>
                {/* outer glow */}
                <circle cx={n.x} cy={n.y} r="32" fill={n.color} opacity="0.08">
                  <animate attributeName="r" values="26;34;26" dur="3s" begin={`${(i * 0.15) % 2}s`} repeatCount="indefinite" />
                </circle>
                <circle cx={n.x} cy={n.y} r="20" fill="#0F1C40" stroke={n.color} strokeWidth="1.5" opacity="0.9" />
                <circle cx={n.x} cy={n.y} r="5" fill={n.color}>
                  <animate attributeName="r" values="4;6.5;4" dur="1.8s" begin={`${(i * 0.1) % 1.5}s`} repeatCount="indefinite" />
                </circle>
                {/* label */}
                <text
                  x={n.x}
                  y={n.y + 42}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#F1EDE4"
                  fontFamily="Inter, sans-serif"
                  fontWeight="500"
                >
                  {n.label}
                </text>
                <text
                  x={n.x}
                  y={n.y + 57}
                  textAnchor="middle"
                  fontSize="9"
                  fill={n.color}
                  fontFamily="JetBrains Mono, monospace"
                >
                  S{String(i + 1).padStart(2, '0')}
                </text>
              </g>
            ))}
          </svg>

          {/* Bottom legend strip */}
          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <span>15 nodes · continuously connected</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4ECDC4] pulse-dot" />
              live signal propagation
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
